<template>
	<div class="notification_wrap-desktop">
		<ul class="notification-desktop">
			<li class="notification_title-desktop">
				<button type="button" class="title_btn" @click="onToggleNotification">
					<img src="@/assets/img/common/arrow_prev.svg" alt="닫기" loading="lazy" />
				</button>
				<h2 class="title_txt">알림</h2>
			</li>
			<div v-if="myPushData" class="notification_body c_inner">
				<ul v-if="myPushData.push_data.length > 0" class="body_list">
					<li
						v-for="(item, idx) in myPushData?.push_data"
						:key="idx"
						class="list_item"
						:class="item.push_send_status_confirm_yn && 'checked'"
					>
						<div class="list_container" @click="onClickAlarm(item.push_send_status_pk, item.push_url)">
							<div class="list_content">
								<p class="content_date">{{ item.push_send_date?.split(' ')[0] }}</p>
								<p class="content_title">
									{{ item.push_title }}
								</p>
								<p class="content_description">
									{{ item.push_content }}
								</p>
							</div>
							<img
								src="@/assets/img/common/arrow_next.svg"
								class="list_icon"
								alt="notification_detail"
								loading="lazy"
							/>
						</div>
					</li>
					<p class="notice">알림은 7일간 보관 후 자동으로 삭제됩니다.</p>
				</ul>
				<p v-if="myPushData.push_data.length === 0" class="notice">새로운 알림이 없습니다.</p>
			</div>
		</ul>
	</div>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useConfirmPushApi, useGetMyPushApi } from '@/api/notification';
// store
import { useHeaderStore } from '@/stores/header';
import { closeMenuModal } from '@/utils/function/modalMenu';

// -----variable-----
const route = useRoute();
const headerStore = useHeaderStore();
const notificationPk = ref(-1);
// -----api-----
const { data: myPushData, refetch: refetchMyPush } = useGetMyPushApi();
const { refetch: refetchConfirmPush } = useConfirmPushApi(
	computed(() => {
		return { push_send_status_pk: notificationPk.value };
	})
);

// -----function-----
// 알림 토글 함수
const onToggleNotification = () => {
	closeMenuModal();
};
// 푸시 이벤트 클릭 시
const onClickAlarm = async (pk: number, url: string) => {
	notificationPk.value = pk;
	await refetchConfirmPush();

	const removeMasterpickPrefix = (str: string) => {
		const prefix = 'masterpick://';
		if (str.startsWith(prefix)) {
			return str.slice(prefix.length);
		}
		return str;
	};

	const result = removeMasterpickPrefix(url);

	const resultURL = `https://masterpick.co.kr/${result}`;
	window.location.href = resultURL;
};
const checkNotification = () => {
	let notificationState = false;
	if (myPushData?.value?.push_data) {
		for (const item of myPushData.value.push_data) {
			if (item.push_send_status_confirm_yn === 0) {
				notificationState = true;
				break;
			}
		}
	}
	headerStore.setIsNotificationExist(notificationState);
};

// -----composition-----
onMounted(async () => {
	await refetchMyPush();
	checkNotification();
});

// route가 변경될 때마다 알림 데이터 업데이트
watch(
	() => route.name,
	async () => {
		await refetchMyPush();
		checkNotification();
	}
);
// 화면 나갔을 경우, 스크롤 방지 기능 삭제
onUnmounted(() => {
	document.body.classList.remove('hidden');
});
</script>

<style scoped lang="scss">
// desktop
.notification_wrap-desktop {
	position: absolute;
	top: 7rem;
	right: -10rem;
}
.notification-desktop {
	z-index: 80;
	width: 37.5rem;
	min-height: 200px;
	max-height: 800px;
	overflow-y: auto;
	max-height: 60.8rem;
	&::-webkit-scrollbar {
		width: 4px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background-color: #818393;
		border-radius: 10px;
	}
	background-color: var(--color-bg-mid);
	border-radius: var(--border-radius-small);
	box-shadow: 4px 4px 10px 0px #0000004d;
	.notification_title-desktop {
		display: flex;
		align-items: center;
		font-weight: var(--font-w-semi-bold);
		gap: var(--space-xx-small);
		padding: 1.85rem var(--space-large);
		border-bottom: 1px solid #393d45;
		.title_btn {
			img {
				width: 2.6rem;
				height: 2.6rem;
			}
		}
		.title_txt {
			font-size: var(--font-s-large);
		}
	}
	.item_title_side {
		display: flex;
		flex-direction: row;
		align-items: center;
		padding: 1.6rem 2rem;
		gap: var(--space-x-small);
		font-weight: var(--font-w-medium);
		font-size: var(--font-s-regular);
	}
}

// mobile
.slide-leave-to,
.slide-enter-from {
	transform: translateX(100%);
}
.slide-leave-from,
.slide-enter-to {
	transform: translateX(0);
}
.slide-enter-active {
	transition: transform 0.5s ease-in-out;
}
.slide-leave-active {
	transform: translateX(100%);
	transition: transform 0.5s ease-in-out;
}
.notification-mobile {
	position: fixed;
	float: right;
	top: 0;
	right: 0;
	width: 100%;
	height: 100dvh;
	background-color: var(--color-bg);
	box-shadow: 0.4rem 0.4rem 1.5rem 0rem #5a5a5a;
	&::-webkit-scrollbar {
		display: none;
	}
	overflow-y: auto;
	color: var(--color-base-white);
	.notification_header-mobile {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--space-xx-small);
		width: 100%;
		height: 6.4rem;
		font-size: var(--font-s-large);
		font-weight: var(--font-w-semi-bold);
		line-height: 2.7rem;
		padding: 1.85rem var(--space-large);
		box-shadow: 4px 4px 10px rgb(0, 0, 0, 0.4);
		.btn_back {
			display: flex;
			gap: 0.4rem;
			img {
				width: 2.6rem;
			}
		}
		.notification_quickMenu-mobile {
			display: flex;
			gap: var(--space-x-small);
			.btn_toggle {
				display: flex;
				gap: 0.05rem;
				img {
					width: 2.4rem;
				}
			}
			.btn_home {
				img {
					width: 2.4rem;
					margin-bottom: 2px;
				}
			}
		}
	}
}

// common
.notification_body {
	padding-bottom: var(--space-mid);
	.body_list {
		.list_item {
			cursor: pointer;
		}
		.checked {
			opacity: 0.5;
		}
		.list_container {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 2rem 0;
			border-bottom: 1px solid #393d45;
			.list_content {
				width: 100%;
				.content_date {
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: 1.551rem;
					color: #818393;
					margin-bottom: 0.8rem;
					white-space: normal;
				}
				.content_title {
					font-size: 1.7rem;
					font-weight: var(--font-w-semi-bold);
					line-height: 2.1rem;
					white-space: normal;
				}
				.content_description {
					font-size: var(--font-s-mid);
					font-weight: var(--font-w-light);
					line-height: 1.9rem;
					white-space: normal;
				}
			}
		}
		.list_icon {
			width: 2rem;
			height: 2rem;
		}
	}
	.notice {
		display: flex;
		justify-content: center;
		font-size: 1.7rem;
		font-weight: 400;
		color: #818393;
		padding: 2.4rem 0;
		margin-top: 2.4rem;
	}
	.notice.noAlert {
		align-items: center;
	}
}
.notification_body.mobile {
	padding-top: var(--space-xx-large);
	.notice.noAlert {
		margin: 0;
		height: calc(100dvh - var(--header-height) - var(--bottomMenu-height) - 5rem);
	}
}
</style>
